import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {customFetcher} from "./customFetcher";
import {cleaningFeeSchema, ICleaningFee} from "../Interfaces/ICleaningFees";

const CLEANING_FEE_QUERY_KEY = ['cleaningFee'];

export const useGetCleaningFees = ({ includeDeleted = false }: { includeDeleted?: boolean } = {}) => {
    const { data: cleaningFeeArray, isLoading: isLoadingCleaningFeeArray } = useQuery<ICleaningFee[]>({
        refetchOnWindowFocus: false,
        queryKey: [...CLEANING_FEE_QUERY_KEY, includeDeleted],
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({
                endpoint: `/cleaningfees/${includeDeleted ? '?include_deleted=true' : ''}`,
            });
            return cleaningFeeSchema.array().parse(data);
        },
        initialData: [],
    });

    return { cleaningFeeArray: cleaningFeeArray as ICleaningFee[], isLoadingCleaningFeeArray };

};

export const useCreateCleaningFee = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createCleaningFee, isPending: isCreatingCleaningFee } = useMutation<ICleaningFee, Error, Omit<ICleaningFee, 'id'>>({
        mutationFn: (newCleaningFee) =>
            customFetcher<ICleaningFee>({
                endpoint: '/cleaningfees/',
                method: 'POST',
                body: newCleaningFee,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: CLEANING_FEE_QUERY_KEY });
        },
    });
    return { createCleaningFee, isCreatingCleaningFee };
};

export const useUpdateCleaningFee = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateCleaningFee, isPending: isUpdatingCleaningFee } = useMutation<ICleaningFee, Error, ICleaningFee>({
        mutationFn: (updatedCleaningFee: ICleaningFee) =>
            customFetcher<ICleaningFee>({
                endpoint: `/cleaningfees/${updatedCleaningFee.id}/`,
                method: 'PUT',
                body: updatedCleaningFee,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: CLEANING_FEE_QUERY_KEY });
        },
    });
    return { updateCleaningFee, isUpdatingCleaningFee };
};

export const useDeleteCleaningFee = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteCleaningFee, isPending: isDeletingCleaningFee } = useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await customFetcher({
                endpoint: `/cleaningfees/${id}/`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: CLEANING_FEE_QUERY_KEY });
        },
    });
    return { deleteCleaningFee, isDeletingCleaningFee };
};
